import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDatainstance from "../../variables/api/axiosMultiPartDatainstance";
import isAuthenticated from "../../services/authService";
import "../../assets/styles/mobileAppPages.css";
import {
  downloadUrlForRelease,
  formatDate,
  formatFileSize,
  releaseIsEmpty,
} from "./mobileAppUtils";

const PageLayout = ({ children }) => (
  <div className="layout-wrapper layout-content-navbar">
    <div className="layout-container">
      <SideBar />

      <div className="layout-page">
        <NavBar />

        <div className="content-wrapper">
          <div className="container-xxl flex-grow-1 container-p-y">
            {children}
          </div>

          <Footer />
          <div className="content-backdrop fade"></div>
        </div>
      </div>
    </div>

    <div className="layout-overlay layout-menu-toggle"></div>
    <div className="drag-target"></div>
  </div>
);

const MobileAppReleasesAdmin = () => {
  const [formValues, setFormValues] = useState({
    versionName: "",
    versionCode: "",
    releaseNotes: "",
  });
  const [file, setFile] = useState(null);
  const [uploadState, setUploadState] = useState("idle"); // idle | uploading
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);
  const [latest, setLatest] = useState(null);
  const [loadingLatest, setLoadingLatest] = useState(false);

  const authed = isAuthenticated();

  const fetchLatest = async () => {
    try {
      setLoadingLatest(true);
      const res = await axiosInstance.get("/api/mobile-app/releases/latest");
      setLatest(releaseIsEmpty(res?.data) ? null : res.data);
      setError("");
    } catch (err) {
      setError("Impossible de récupérer la dernière version.");
    } finally {
      setLoadingLatest(false);
    }
  };

  useEffect(() => {
    fetchLatest();
  }, []);

  const handleChange = (evt) => {
    const { name, value } = evt.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (evt) => {
    setFile(evt.target.files?.[0] || null);
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (!file) {
      setError("Le fichier APK est obligatoire.");
      return;
    }
    if (!formValues.versionName.trim() || !formValues.versionCode.trim()) {
      setError("versionName et versionCode sont obligatoires.");
      return;
    }

    setUploadState("uploading");
    setError("");
    setSuccessData(null);
    setProgress(0);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("versionName", formValues.versionName.trim());
    formData.append("versionCode", parseInt(formValues.versionCode, 10));
    if (formValues.releaseNotes.trim()) {
      formData.append("releaseNotes", formValues.releaseNotes.trim());
    }

    try {
      const response = await axiosMultiPartDatainstance.post(
        "/api/mobile-app/releases",
        formData,
        {
          onUploadProgress: (event) => {
            if (!event.total) return;
            const percent = Math.round((event.loaded * 100) / event.total);
            setProgress(percent);
          },
        }
      );
      setSuccessData(response.data);
      setFormValues({ versionName: "", versionCode: "", releaseNotes: "" });
      setFile(null);
      setProgress(100);
      fetchLatest();
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message || err?.response?.data?.error || err?.message;
      setError(backendMessage || "Le téléversement a échoué.");
    } finally {
      setUploadState("idle");
    }
  };

  const notes = useMemo(
    () =>
      latest?.releaseNotes
        ? latest.releaseNotes
            .split(/\n|•/g)
            .map((item) => item.trim())
            .filter(Boolean)
        : [],
    [latest]
  );

  if (!authed) {
    return (
      <PageLayout>
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center">
                <h3 className="mb-2">Accès refusé</h3>
                <p className="mb-3">Cette page est réservée aux administrateurs authentifiés.</p>
                <p className="mb-0">
                  <a href="/admin/login" className="btn btn-primary me-2">
                    Aller vers /admin/login
                  </a>
                  <Link to="/connexion" className="btn btn-label-secondary">
                    Ouvrir la page de connexion
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Application mobile</h4>
              <p className="text-muted mb-0">Publiez les fichiers APK depuis l'espace administrateur.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Téléverser une nouvelle version Android</h5>
            </div>
            <div className="card-body">
              <form className="mobile-app-form" onSubmit={handleSubmit}>
                <div className="inline">
                  <label>
                    Nom de Version *
                    <input
                      name="versionName"
                      value={formValues.versionName}
                      onChange={handleChange}
                      placeholder="ex: 1.2.0"
                      required
                    />
                  </label>
                  <label>
                   Code de la version  *
                    <input
                      name="versionCode"
                      type="number"
                      value={formValues.versionCode}
                      onChange={handleChange}
                      placeholder="ex: 34"
                      required
                      min={1}
                    />
                  </label>
                </div>
                <label>
                  Fichier APK *
                  <input type="file" accept=".apk" onChange={handleFileChange} required />
                </label>
                <label>
                  Notes de version (optionnel)
                  <textarea
                    name="releaseNotes"
                    value={formValues.releaseNotes}
                    onChange={handleChange}
                    placeholder="Nouveautés, correctifs..."
                  />
                </label>

                {uploadState === "uploading" && (
                  <div className="mobile-app-progress" aria-label="Progression de l'upload">
                    <span style={{ width: `${progress}%` }} />
                  </div>
                )}

                {error && <div className="mobile-app-status error">{error}</div>}
                {successData && (
                  <div className="mobile-app-status success">
                    Nouvelle version publiée : {successData?.versionName || "OK"} (code{" "}
                    {successData?.versionCode || "-"})
                  </div>
                )}

                <div className="mobile-app-cta-row" style={{ justifyContent: "flex-start" }}>
                  <button
                    type="submit"
                    className="mobile-app-button primary"
                    disabled={uploadState === "uploading"}
                  >
                    {uploadState === "uploading" ? "Téléversement..." : "Publier la version"}
                  </button>
                  <button
                    type="button"
                    className="mobile-app-button"
                    onClick={fetchLatest}
                    disabled={loadingLatest}
                  >
                    {loadingLatest ? "Lecture..." : "Voir la dernière version"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h5 className="mb-0">Dernière version publiée</h5>
              <button
                type="button"
                className="btn btn-label-secondary"
                onClick={fetchLatest}
                disabled={loadingLatest}
              >
                {loadingLatest ? "Lecture..." : "Actualiser"}
              </button>
            </div>
            <div className="card-body">
              {loadingLatest && <div className="mobile-app-status">Chargement...</div>}
              {!loadingLatest && !latest && (
                <div className="mobile-app-status">Aucune version publiée pour le moment.</div>
              )}
              {latest && (
                <div className="mobile-app-latest">
                  <div className="mobile-app-meta">
                    <div className="meta-item">
                      <div className="meta-label">Version</div>
                      <div className="meta-value">{latest.versionName}</div>
                      <div className="mobile-app-tagline">Code {latest.versionCode}</div>
                    </div>
                    <div className="meta-item">
                      <div className="meta-label">Taille</div>
                      <div className="meta-value">{formatFileSize(latest.fileSize)}</div>
                    </div>
                    <div className="meta-item">
                      <div className="meta-label">Mise à jour</div>
                      <div className="meta-value">{formatDate(latest.uploadedAt)}</div>
                    </div>
                    <div className="meta-item">
                      <div className="meta-label">Checksum</div>
                      <div className="meta-value" style={{ wordBreak: "break-all" }}>
                        {latest.checksumSha256 || "Non fourni"}
                      </div>
                    </div>
                  </div>
                  {notes.length > 0 && (
                    <div className="mobile-app-notes">
                      {notes.map((note) => (
                        <div key={note}>• {note}</div>
                      ))}
                    </div>
                  )}
                  <a className="mobile-app-chip" href={downloadUrlForRelease(latest)} download>
                    Télécharger cette version
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default MobileAppReleasesAdmin;
